import { ReactNode, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { Center, Loader } from '@mantine/core';
import { loginRequest } from './authConfig';
import { useLocalAuth } from './local-auth';

interface Props {
  children: ReactNode;
}

const USE_MOCK_AUTH = import.meta.env.VITE_ENABLE_MOCKS === 'true';

/**
 * Guard que acepta sesiones tanto locales (signup/login email+password)
 * como MSAL (Entra ID real o mock de dev).
 *
 * - Si hay sesion local activa, pasa.
 * - Si no, consulta MSAL (o mock de dev).
 * - Si tampoco, redirige a /login (login unificado: email+password
 *   y/o Continuar con Microsoft).
 */
export function AuthGuard({ children }: Props) {
  const local = useLocalAuth();
  const isMsalAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  const location = useLocation();

  const isAuthenticated = local.isAuthenticated || (USE_MOCK_AUTH ? true : isMsalAuthenticated);

  useEffect(() => {
    if (!isAuthenticated && accounts.length === 0) {
      // No-op: el render devolvera <Navigate>
    }
  }, [isAuthenticated, accounts]);

  // Si estamos con MSAL pero sin scopes, los pedimos en background.
  useEffect(() => {
    const hasScopes = Object.prototype.hasOwnProperty.call(accounts[0]?.idTokenClaims ?? {}, 'scp');

    if (!local.isAuthenticated && accounts[0] && !hasScopes) {
      instance.acquireTokenRedirect({ ...loginRequest, account: accounts[0] });
    }
  }, [accounts, instance, local.isAuthenticated]);

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

export function LoadingScreen() {
  return (
    <Center h="100vh">
      <Loader size="lg" />
    </Center>
  );
}
